/**
 * Compact sun/moon toggle that flips between `light` and `dark`.
 * Lives next to the avatar in both AdminShell and AppShell.
 *
 * For three-way `light | dark | system` selection users go to Profile.
 * One-tap toggle in the header is the highest-leverage interaction;
 * `system` is the parking-lot option for power users.
 */
import { useTheme } from "@/components/theme/useTheme";

export function ThemeToggle(): JSX.Element {
  const { effective, setPreference } = useTheme();
  const next = effective === "dark" ? "light" : "dark";
  return (
    <button
      type="button"
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      onClick={() => setPreference(next)}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-brand-100/60 dark:text-text-primary dark:hover:bg-white/5"
    >
      {effective === "dark" ? (
        // Sun (currently dark → tap to go light)
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // Moon (currently light → tap to go dark)
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
        </svg>
      )}
    </button>
  );
}
